import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ConstructionContractsService } from '../services/constructionContracts.service';
import type { ConstructionContract, ContractFilters } from '../types/construction-contracts.types';
import { useState, useEffect, useMemo } from 'react';

export function useConstructionContracts(companyId: string, initialFilters: ContractFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ContractFilters>({
    page: 1,
    pageSize: 10,
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const filterRef = JSON.stringify(initialFilters);
  useEffect(() => {
    setFilters(prev => ({ ...prev, ...initialFilters }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRef]);

  const query = useQuery({
    queryKey: ['construction-contracts', companyId, filters],
    queryFn: () => ConstructionContractsService.getContracts(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['construction-contract-stats', companyId],
    queryFn: () => ConstructionContractsService.getContractStats(companyId),
    enabled: !!companyId,
  });

  const createMutation = useMutation({
    mutationFn: (data: Partial<ConstructionContract>) => ConstructionContractsService.createContract(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['construction-contract-stats'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string, updates: Partial<ConstructionContract> }) => 
      ConstructionContractsService.updateContract(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['construction-contract-stats'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ConstructionContractsService.deleteContract(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['construction-contracts'] });
      queryClient.invalidateQueries({ queryKey: ['construction-contract-stats'] });
    },
  });

  return {
    contracts: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data || { totalValue: 0, activeContracts: 0, pendingValue: 0, avgRetentionRate: 0 },
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    createContract: createMutation.mutateAsync,
    updateContract: updateMutation.mutateAsync,
    deleteContract: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
